import type {
  ProjectLevel,
  ProjectStatus,
} from "@/modules/management/project/domain/types";
import { config } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  type LookupItem,
} from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";

export type ProjectSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  status?: ProjectStatus;
  level?: ProjectLevel;
  departmentId?: string;
  positionId?: string;
  responsibleUserId?: string;
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  search?: string;
};

const VALUE_ALL = "__all__";

const STATUS_OPTIONS = [
  { value: VALUE_ALL, label: "ທັງໝົດ" },
  { value: "active", label: "ກຳລັງດຳເນີນການ" },
  { value: "completed", label: "ສຳເລັດ" },
  { value: "on_hold", label: "ຢຸດ" },
];

const LEVEL_TAB_VALUES = [
  { value: VALUE_ALL, label: "ທັງໝົດ" },
  { value: "organization", label: "ລະດັບອົງກອນ" },
  { value: "department", label: "ລະດັບຜະແນກ" },
  { value: "position", label: "ລະດັບຕຳແໜ່ງ" },
] as const;

type ProjectFilterProps = {
  searchParams: ProjectSearchParams;
  fromRoute: "/app/management/projects";
  /** Optional list of users for responsible filter (id, displayName) */
  responsibleOptions?: Array<{ value: string; label: string }>;
};

export function ProjectFilter({
  searchParams,
  fromRoute,
  responsibleOptions = [],
}: ProjectFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const search = searchParams;

  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [statusInput, setStatusInput] = useState<string>(
    search.status ?? VALUE_ALL,
  );
  const [levelInput, setLevelInput] = useState<string>(
    search.level ?? VALUE_ALL,
  );
  const [departmentIdInput, setDepartmentIdInput] = useState(
    search.departmentId ?? "",
  );
  const [positionIdInput, setPositionIdInput] = useState(
    search.positionId ?? "",
  );
  const [responsibleInput, setResponsibleInput] = useState(
    search.responsibleUserId ?? VALUE_ALL,
  );
  const [startFrom, setStartFrom] = useState(search.startDateFrom ?? "");
  const [startTo, setStartTo] = useState(search.startDateTo ?? "");
  const [endFrom, setEndFrom] = useState(search.endDateFrom ?? "");
  const [endTo, setEndTo] = useState(search.endDateTo ?? "");

  useEffect(() => {
    setSearchInput(search.search ?? "");
    setStatusInput(search.status ?? VALUE_ALL);
    setLevelInput(search.level ?? VALUE_ALL);
    setDepartmentIdInput(search.departmentId ?? "");
    setPositionIdInput(search.positionId ?? "");
    setResponsibleInput(search.responsibleUserId ?? VALUE_ALL);
    setStartFrom(search.startDateFrom ?? "");
    setStartTo(search.startDateTo ?? "");
    setEndFrom(search.endDateFrom ?? "");
    setEndTo(search.endDateTo ?? "");
  }, [
    search.search,
    search.status,
    search.level,
    search.departmentId,
    search.positionId,
    search.responsibleUserId,
    search.startDateFrom,
    search.startDateTo,
    search.endDateFrom,
    search.endDateTo,
  ]);

  const limit = search.limit ?? 20;

  const apply = (
    opts: Partial<{
      searchVal: string;
      statusVal: string;
      levelVal: string;
      departmentIdVal: string;
      positionIdVal: string;
      responsibleVal: string;
      startDateFrom: string;
      startDateTo: string;
      endDateFrom: string;
      endDateTo: string;
    }>,
  ) => {
    const searchTerm = (opts.searchVal ?? searchInput).trim() || undefined;
    const status =
      (opts.statusVal ?? statusInput) === VALUE_ALL
        ? undefined
        : ((opts.statusVal ?? statusInput) as ProjectStatus | undefined);
    const level =
      (opts.levelVal ?? levelInput) === VALUE_ALL
        ? undefined
        : ((opts.levelVal ?? levelInput) as ProjectLevel | undefined);
    const departmentId =
      (opts.departmentIdVal ?? departmentIdInput)?.trim() || undefined;
    const positionId =
      (opts.positionIdVal ?? positionIdInput)?.trim() || undefined;
    const responsibleUserId =
      (opts.responsibleVal ?? responsibleInput) === VALUE_ALL ||
      !(opts.responsibleVal ?? responsibleInput)
        ? undefined
        : (opts.responsibleVal ?? responsibleInput);
    const startDateFrom = (opts.startDateFrom ?? startFrom) || undefined;
    const startDateTo = (opts.startDateTo ?? startTo) || undefined;
    const endDateFrom = (opts.endDateFrom ?? endFrom) || undefined;
    const endDateTo = (opts.endDateTo ?? endTo) || undefined;

    nav({
      search: {
        offset: 0,
        limit,
        sort: search.sort,
        status,
        level,
        departmentId: level === "department" ? departmentId : undefined,
        positionId: level === "position" ? positionId : undefined,
        responsibleUserId,
        startDateFrom,
        startDateTo,
        endDateFrom,
        endDateTo,
        search: searchTerm,
      },
    });
  };

  const applySearch = (searchVal: string) => {
    setSearchInput(searchVal);
    apply({ searchVal });
  };
  const debouncedApplySearch = useDebounceCallback(applySearch, 400);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    debouncedApplySearch(val);
  };

  const responsibleSelectOptions = [
    { value: VALUE_ALL, label: "ທັງໝົດ" },
    ...responsibleOptions,
  ];

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="project-filter">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="project-search"
            aria-label="ຄົ້ນຫາຕາມຊື່ໂຄງການ"
            placeholder="ຄົ້ນຫາຊື່ໂຄງການ..."
            className="h-8 pl-8 sm:max-w-2xs"
            defaultValue={searchInput}
            onChange={handleSearchChange}
            onKeyDown={(e) => e.key === "Enter" && applySearch(searchInput)}
          />
        </div>
        <SimpleSelect
          options={STATUS_OPTIONS}
          value={statusInput}
          onValueChange={(v) => {
            setStatusInput(v);
            apply({ statusVal: v });
          }}
          placeholder="ສະຖານະ"
          className="h-8 w-full sm:w-36"
          aria-label="ສະຖານະ"
        />
        <Tabs
          value={levelInput}
          onValueChange={(v) => {
            setLevelInput(v);
            if (v !== "department") setDepartmentIdInput("");
            if (v !== "position") setPositionIdInput("");
            apply({
              levelVal: v,
              departmentIdVal: v === "department" ? departmentIdInput : "",
              positionIdVal: v === "position" ? positionIdInput : "",
            });
          }}
          className="w-full sm:w-auto"
          aria-label="ລະດັບໂຄງການ"
        >
          <TabsList className="h-8">
            {LEVEL_TAB_VALUES.map((opt) => (
              <TabsTrigger
                key={opt.value}
                value={opt.value}
                className="text-xs sm:text-sm"
              >
                {opt.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {levelInput === "department" && (
          <InfiniteCombobox<LookupItem>
            queryKey={["departments", "lookup", "project-filter"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentIdInput}
            onValueChange={(v) => {
              setDepartmentIdInput(v);
              apply({ departmentIdVal: v });
            }}
            placeholder="ເລືອກແຜ່ນການ..."
            className="h-8 w-full sm:w-44"
            clearable
            aria-label="ແຜ່ນການ"
          />
        )}
        {levelInput === "position" && (
          <InfiniteCombobox<LookupItem>
            queryKey={["positions", "lookup", "project-filter"]}
            queryFn={(args) =>
              fetchLookupForInfinite(`${config.apiUrl}/positions/lookup`, args)
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={positionIdInput}
            onValueChange={(v) => {
              setPositionIdInput(v);
              apply({ positionIdVal: v });
            }}
            placeholder="ເລືອກຕຳແໜ່ງ..."
            className="h-8 w-full sm:w-44"
            clearable
            aria-label="ຕຳແໜ່ງ"
          />
        )}
        {responsibleOptions.length > 0 && (
          <SimpleSelect
            options={responsibleSelectOptions}
            value={responsibleInput}
            onValueChange={(v) => {
              setResponsibleInput(v);
              apply({ responsibleVal: v });
            }}
            placeholder="ຜູ້ຮັບຜິດຊອບ"
            className="h-8 w-full sm:w-44"
            aria-label="ຜູ້ຮັບຜິດຊອບ"
          />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">ວັນເລີ່ມ:</span>
        <Input
          type="date"
          className="h-8 w-full sm:w-40"
          value={startFrom}
          onChange={(e) => {
            setStartFrom(e.target.value);
            apply({ startDateFrom: e.target.value || undefined });
          }}
          aria-label="ວັນເລີ່ມຈາກ"
        />
        <span className="text-muted-foreground">–</span>
        <Input
          type="date"
          className="h-8 w-full sm:w-40"
          value={startTo}
          onChange={(e) => {
            setStartTo(e.target.value);
            apply({ startDateTo: e.target.value || undefined });
          }}
          aria-label="ວັນເລີ່ມຫາ"
        />
        <span className="mx-1 text-muted-foreground">|</span>
        <span className="text-muted-foreground">ວັນສິ້ນສຸດ:</span>
        <Input
          type="date"
          className="h-8 w-full sm:w-40"
          value={endFrom}
          onChange={(e) => {
            setEndFrom(e.target.value);
            apply({ endDateFrom: e.target.value || undefined });
          }}
          aria-label="ວັນສິ້ນສຸດຈາກ"
        />
        <span className="text-muted-foreground">–</span>
        <Input
          type="date"
          className="h-8 w-full sm:w-40"
          value={endTo}
          onChange={(e) => {
            setEndTo(e.target.value);
            apply({ endDateTo: e.target.value || undefined });
          }}
          aria-label="ວັນສິ້ນສຸດຫາ"
        />
      </div>
    </div>
  );
}
